import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Undo2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Pagination } from '@/components/ui/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuthStore } from '@/features/auth/auth.store';
import { useBusinesses } from '@/features/business/use-businesses';
import { formatCurrency, formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  PAGE_SIZE,
  usePurchases,
  useRevertTransaction,
  useTransactions,
  type DateRange,
} from './gift-cards.api';
import { dateFields } from './date-filters';
import { PeriodSummary, TabStates } from './tab-shell';
import { TRANSACTION_TYPE, type GiftCardTransaction } from './types';

/**
 * The two ledger tabs.
 *
 * They look alike and are not the same query: Transactions is every movement
 * in both directions from `giftcards/history`, Purchases is loads only from
 * `giftcards/purchase/report`, which is the one that can be filtered by store
 * and is what finance reconciles against a register.
 */

const ANY = 'ANY';

/** Money on is a credit, money off a debit; the sign is the point of the row. */
function AmountCell({ type, amount }: { type: number; amount: number }) {
  const { t } = useTranslation();
  const isLoad = type === TRANSACTION_TYPE.load;
  const magnitude = Math.abs(amount);

  return (
    <span
      className={
        isLoad ? 'text-success tabular-nums' : 'text-foreground tabular-nums'
      }
      title={isLoad ? t('giftCards.load') : t('giftCards.redemption')}
    >
      {isLoad ? '+' : '−'}
      {formatCurrency(magnitude).replace(/^-/, '')}
    </span>
  );
}

export function TransactionsTab({
  dates,
  onDatesChange,
  onOpen,
}: {
  dates: DateRange;
  onDatesChange: (next: DateRange) => void;
  onOpen: (card: { id: string; code: string | null }) => void;
}) {
  const { t } = useTranslation();
  const canReverse = useAuthStore((s) => s.can('canReverseGiftCards'));

  const [page, setPage] = useState(0);
  const [reverting, setReverting] = useState<GiftCardTransaction | null>(null);

  const list = useTransactions({ dates, page });
  const revert = useRevertTransaction();

  const rows = list.data?.transactions ?? [];
  const total = list.data?.total ?? 0;

  const fields: FilterField[] = dateFields(
    dates,
    (next) => {
      onDatesChange(next);
      setPage(0);
    },
    t
  );

  return (
    <div className="space-y-4 pt-4">
      <FilterBar
        busy={list.isFetching}
        fields={fields}
        summary={
          list.isSuccess ? (
            <PeriodSummary
              totals={list.data}
              shown={rows.length}
              total={total}
            />
          ) : null
        }
      />

      <TabStates
        isError={list.isError}
        isPending={list.isPending}
        isEmpty={list.isSuccess && rows.length === 0}
        busy={list.isFetching}
        emptyMessage={t('giftCards.noTransactions')}
        onRetry={() => void list.refetch()}
      >
        {rows.length > 0 && (
          <>
            <div className="hidden rounded-xl border lg:block">
              <Table busy={list.isFetching}>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>{t('giftCards.when')}</TableHead>
                    <TableHead>{t('giftCards.code')}</TableHead>
                    <TableHead>{t('giftCards.order')}</TableHead>
                    <TableHead>{t('giftCards.store')}</TableHead>
                    <TableHead>{t('giftCards.type')}</TableHead>
                    <TableHead className="text-right">
                      {t('giftCards.amount')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('giftCards.balanceAfter')}
                    </TableHead>
                    {canReverse && <TableHead className="w-24 text-right" />}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="whitespace-nowrap">
                        {formatDateTime(row.createdAt)}
                      </TableCell>
                      <TableCell>
                        <button
                          type="button"
                          className="hover:text-primary truncate font-mono text-xs underline-offset-2 hover:underline"
                          onClick={() =>
                            row.giftCardId &&
                            onOpen({ id: row.giftCardId, code: row.giftCardCode })
                          }
                        >
                          {row.giftCardCode ?? '—'}
                        </button>
                        {row.giftCardNickname && (
                          <p className="text-muted-foreground truncate text-xs">
                            {row.giftCardNickname}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {row.orderConfirmationCode ?? '—'}
                      </TableCell>
                      <TableCell className="truncate">
                        {row.orderBusinessName ?? '—'}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            row.transactionType === TRANSACTION_TYPE.load
                              ? 'success'
                              : 'secondary'
                          }
                        >
                          {row.transactionType === TRANSACTION_TYPE.load
                            ? t('giftCards.load')
                            : t('giftCards.redemption')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <AmountCell
                          type={row.transactionType}
                          amount={row.amount}
                        />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(row.giftCardCurrentBalance)}
                      </TableCell>
                      {canReverse && (
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setReverting(row)}
                          >
                            <Undo2 className="size-3.5" />
                            {t('giftCards.revert')}
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="relative space-y-2 lg:hidden">
              <BusyOverlay show={list.isFetching} />
              {rows.map((row) => (
                <div
                  key={row.id}
                  className={cn(
                    'bg-card space-y-2 rounded-xl border p-3',
                    list.isFetching && 'opacity-60 transition-opacity'
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <button
                      type="button"
                      className="truncate font-mono text-xs underline-offset-2 hover:underline"
                      onClick={() =>
                        row.giftCardId &&
                        onOpen({ id: row.giftCardId, code: row.giftCardCode })
                      }
                    >
                      {row.giftCardCode ?? '—'}
                    </button>
                    <AmountCell
                      type={row.transactionType}
                      amount={row.amount}
                    />
                  </div>
                  <p className="text-muted-foreground text-xs">
                    {formatDateTime(row.createdAt)} ·{' '}
                    {row.orderBusinessName ?? '—'}
                  </p>
                  {canReverse && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => setReverting(row)}
                    >
                      <Undo2 className="size-3.5" />
                      {t('giftCards.revert')}
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <Pagination
              page={page}
              size={PAGE_SIZE}
              total={total}
              onPageChange={setPage}
            />
          </>
        )}
      </TabStates>

      <AlertDialog
        open={reverting !== null}
        onOpenChange={(next) => {
          if (!next) setReverting(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('giftCards.revert')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('giftCards.revertConfirm', {
                amount: formatCurrency(Math.abs(reverting?.amount ?? 0)),
                code: reverting?.giftCardCode ?? '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() =>
                revert.mutate(reverting!.id, {
                  onSuccess: () => {
                    toast.success(t('giftCards.reverted'));
                    setReverting(null);
                  },
                  onError: (error) =>
                    toast.error(
                      error instanceof Error
                        ? error.message
                        : t('giftCards.actionFailed')
                    ),
                })
              }
            >
              {t('giftCards.revert')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function PurchasesTab({
  dates,
  onDatesChange,
  onOpen,
}: {
  dates: DateRange;
  onDatesChange: (next: DateRange) => void;
  onOpen: (card: { id: string; code: string | null }) => void;
}) {
  const { t } = useTranslation();

  const [businessId, setBusinessId] = useState(ANY);
  const [page, setPage] = useState(0);

  const { data: businesses } = useBusinesses();
  const list = usePurchases({
    dates,
    businessId: businessId === ANY ? null : businessId,
    page,
  });

  const rows = list.data?.transactions ?? [];
  const total = list.data?.total ?? 0;

  const fields: FilterField[] = [
    {
      id: 'store',
      label: t('giftCards.store'),
      value: businessId,
      onChange: (value) => {
        setBusinessId(value);
        setPage(0);
      },
      options: [
        { value: ANY, label: t('giftCards.allStores') },
        ...(businesses ?? []).map((business) => ({
          value: business.id,
          label: business.name,
        })),
      ],
    },
    ...dateFields(
      dates,
      (next) => {
        onDatesChange(next);
        setPage(0);
      },
      t
    ),
  ];

  return (
    <div className="space-y-4 pt-4">
      <FilterBar
        busy={list.isFetching}
        fields={fields}
        summary={
          list.isSuccess ? (
            <p className="text-muted-foreground text-sm" aria-live="polite">
              {t('giftCards.showingOf', { shown: rows.length, total })}
            </p>
          ) : null
        }
      />

      <TabStates
        isError={list.isError}
        isPending={list.isPending}
        isEmpty={list.isSuccess && rows.length === 0}
        busy={list.isFetching}
        emptyMessage={t('giftCards.noPurchases')}
        onRetry={() => void list.refetch()}
      >
        {rows.length > 0 && (
          <>
            <div className="hidden rounded-xl border lg:block">
              <Table busy={list.isFetching}>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>{t('giftCards.when')}</TableHead>
                    <TableHead>{t('giftCards.code')}</TableHead>
                    <TableHead>{t('giftCards.order')}</TableHead>
                    <TableHead>{t('giftCards.store')}</TableHead>
                    <TableHead className="text-right">
                      {t('giftCards.amount')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('giftCards.balanceAfter')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="whitespace-nowrap">
                        {formatDateTime(row.date)}
                      </TableCell>
                      <TableCell>
                        <button
                          type="button"
                          className="hover:text-primary font-mono text-xs underline-offset-2 hover:underline"
                          onClick={() =>
                            row.giftCardId &&
                            onOpen({ id: row.giftCardId, code: row.code })
                          }
                        >
                          {row.code ?? '—'}
                        </button>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {row.confirmationCode ?? '—'}
                      </TableCell>
                      <TableCell className="truncate">
                        {row.storeName ?? '—'}
                      </TableCell>
                      <TableCell className="text-success text-right tabular-nums">
                        {formatCurrency(Math.abs(row.amount))}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(row.giftCardCurrentBalance)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="relative space-y-2 lg:hidden">
              <BusyOverlay show={list.isFetching} />
              {rows.map((row) => (
                <div
                  key={row.id}
                  className={cn(
                    'bg-card space-y-2 rounded-xl border p-3',
                    list.isFetching && 'opacity-60 transition-opacity'
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <button
                      type="button"
                      className="truncate font-mono text-xs underline-offset-2 hover:underline"
                      onClick={() =>
                        row.giftCardId &&
                        onOpen({ id: row.giftCardId, code: row.code })
                      }
                    >
                      {row.code ?? '—'}
                    </button>
                    <span className="text-success shrink-0 tabular-nums">
                      {formatCurrency(Math.abs(row.amount))}
                    </span>
                  </div>
                  <p className="text-muted-foreground text-xs">
                    {formatDateTime(row.date)} · {row.storeName ?? '—'}
                  </p>
                </div>
              ))}
            </div>

            <Pagination
              page={page}
              size={PAGE_SIZE}
              total={total}
              onPageChange={setPage}
            />
          </>
        )}
      </TabStates>
    </div>
  );
}
